import React, { useEffect, useRef, useState } from 'react'
import { Info, X } from 'lucide-react'
import api from '../../services/api'
import JsonViewer from '../lead-inspector/JsonViewer'
import { fmtFull } from '../lead-inspector/model'
import ReasonPill from './ReasonPill'
import { detailEndpoint } from './model'

// One blocked submission, from GET /admin/leads/blocked/{event_id}, in a
// side panel. The payload is the rejection Event's, masked by the backend:
// intake does not store what the partner submitted, so there is no full
// payload, sub_id or matched lead to show - and none is invented here.
export default function DetailPanel({ eventId, timezone, onClose }) {
  const [tick, setTick] = useState(0)
  const [state, setState] = useState({ data: null, loading: true, error: null })
  const closeRef = useRef(null)

  useEffect(() => {
    let cancelled = false
    setState({ data: null, loading: true, error: null })
    api.get(detailEndpoint(eventId))
      .then(({ data }) => { if (!cancelled) setState({ data, loading: false, error: null }) })
      .catch((error) => { if (!cancelled) setState({ data: null, loading: false, error }) })
    return () => { cancelled = true }
  }, [eventId, tick])

  useEffect(() => {
    closeRef.current?.focus()
    const onKey = (e) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onClose])

  const notFound = state.error?.response?.status === 404
  const d = state.data

  return (
    <>
      <div className="bk-panel-backdrop" aria-hidden="true" onClick={onClose} />
      <aside className="bk-panel" role="dialog" aria-modal="true" aria-labelledby="bk-panel-title">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
          <h2 id="bk-panel-title" style={{ margin: 0, fontSize: 17, fontWeight: 700 }}>Blocked submission</h2>
          <button ref={closeRef} type="button" className="btn btn-ghost btn-sm" onClick={onClose} aria-label="Close details">
            <X size={14} aria-hidden="true" />
          </button>
        </div>

        {state.loading ? (
          <div className="bk-skeleton" aria-busy="true" aria-label="Loading details" style={{ height: 220 }} />
        ) : notFound ? (
          <div role="alert" style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
            <p style={{ margin: '0 0 4px', fontWeight: 600, color: 'var(--text-primary)' }}>Blocked submission not found</p>
            <p style={{ margin: 0 }}>It may have been removed, or the link is wrong.</p>
          </div>
        ) : state.error ? (
          <div role="alert" style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
            <p style={{ margin: '0 0 10px' }}>The details could not be loaded.</p>
            <button type="button" className="btn btn-primary btn-sm" onClick={() => setTick((t) => t + 1)}>Retry</button>
          </div>
        ) : d ? (
          <>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div><ReasonPill reason={d.reason} /></div>
              {d.detail && <p style={{ margin: 0, fontSize: 14, color: 'var(--text-primary)', overflowWrap: 'anywhere' }}>{d.detail}</p>}
            </div>
            <dl className="bk-kv">
              <dt>Received</dt>
              <dd>{fmtFull(d.received_at, timezone)} ET</dd>
              <dt>Partner</dt>
              <dd>{d.partner?.name || '—'}</dd>
              <dt>Event ID</dt>
              <dd className="bk-mono">{d.event_id}</dd>
            </dl>
            <JsonViewer value={d.payload || {}} label="payload" defaultOpen />
            <p style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: 'var(--text-muted)' }}>
              <Info size={12} aria-hidden="true" style={{ flexShrink: 0 }} />
              Only the rejection reason is stored for blocked submissions.
            </p>
          </>
        ) : null}
      </aside>
    </>
  )
}
